import { Paging as PagingClass } from "@pineapplelab/paging";
import { isNotEmptyString, isNullOrUndefined, isValidFunction } from "@pineapplelab/util";
import firebase from "firebase/compat/app";
import { Subject } from "rxjs";

export class Paging<T = any> extends PagingClass<T> {

    /**
     * Fires every time `loadData()` resolves (in ADDITION to the `afterLoadData` hook some
     * callers already use for their own purposes — this is a separate channel so it never
     * clobbers that).
     *
     * Why this exists: the paging package queries the RAW compat Firestore SDK (`ref.firestore`,
     * bypassing AngularFire's own zone-reentry wrappers), whose internal webchannel/fetch/
     * IndexedDB pipeline can resolve the query in a zone that never notifies Angular. Consumers
     * that render `this.response.data` (namely `CrudV2Component`) subscribe to this and run a
     * LOCAL, targeted `ChangeDetectorRef.detectChanges()` on just their own view — which works
     * regardless of which zone the data landed in, and (unlike a global `ApplicationRef.tick()`)
     * can't collide with an unrelated ancestor's mid-transition expression and throw NG0100.
     *
     * NOTE: declared OPTIONAL (`?`) on purpose. Several pages construct their `paging` property
     * using the RAW `Paging` from `@pineapplelab/paging` directly instead of this app wrapper
     * (e.g. `contracts.component.ts`) — a pre-existing inconsistency. `TableV2.paging` is typed
     * against THIS class, so making this a required field would break every one of those call
     * sites at compile time. Keeping it optional preserves structural compatibility with plain
     * `PagingClass` instances (which simply won't have this signal — `CrudV2Component` already
     * guards access with `?.`), while every instance actually constructed via `new Paging(...)`
     * (this class) still always gets one, since it's assigned unconditionally below.
     */
    dataLoaded$?: Subject<void> = new Subject<void>();

    /**
     * Symmetric counterpart to `dataLoaded$`, fired right when a load STARTS instead of when it
     * resolves. Needed for the opposite-direction NG0100 on `CrudV2Component`'s
     * `<mat-spinner *ngIf="showLoading || table?.paging?.loading">` (previous `'false'` → current
     * `'true'`): the base `@pineapplelab/paging` package's `__trueLoadData()` sets `this.loading =
     * true` synchronously right after `loadData()` is called (as long as no `beforeLoadData` hook
     * is registered — true for every page in this app today). That's normally synchronous with
     * whatever zone-patched event triggered the reload (a tab click, a filter change, etc.), so it
     * usually lands in the SAME tick as the triggering event and causes no issue on its own.
     * However, some trigger paths in this codebase (RxJS `debounceTime`-scheduled search updates,
     * dialog `afterClosed()` continuations, etc.) can call `loadData()` from a context where
     * Angular's own tick has ALREADY started checking `CrudV2Component`'s bindings once — the
     * child triggers the mutation mid-parent-tick and `checkNoChanges()` catches the mismatch.
     * Emitting here, synchronously, the instant `loadData()` is invoked (mirroring `dataLoaded$`'s
     * placement at the other end of the call) lets `CrudV2Component` force its OWN view in sync
     * immediately via a LOCAL `ChangeDetectorRef.detectChanges()`, the same established pattern
     * used for `dataLoaded$` — see that field's doc comment above for the full rationale.
     */
    loadStarted$?: Subject<void> = new Subject<void>();

    orderOptions: Array<{ label: string, field: string, placeholder: string, dataType: PagingClass.dataType }> = [
        { field: 'nameSearch', label: 'name', placeholder: 'Filter by name', dataType: Paging.dataType.string }
    ];
    fieldSearch?: { [field: string]: Partial<Paging.IFieldSearch> } = {};
    onFieldSearchChange?: (...args: any[]) => void;
    selectOption?: string = null;

    constructor(paging?: Partial<Paging.IPaging<T>>) {
        super(paging);
        // Expand the default page-size options unless the caller overrides them
        if (!isNullOrUndefined(paging?.sizeOptions)) {
            this.sizeOptions = paging.sizeOptions;
        } else {
            this.sizeOptions = [10, 20, 40, 60, 100, 200];
        }
        if (!isNullOrUndefined(paging?.orderOptions)) {
            this.orderOptions = paging.orderOptions;
        }
        if (!isNullOrUndefined(paging.fieldSearch)) {
            this.fieldSearch = paging.fieldSearch;
        }
        if (isValidFunction(paging.onFieldSearchChange)) {
            this.onFieldSearchChange = paging.onFieldSearchChange;
        }
        if (!isNotEmptyString(paging.selectOption)) {
            this.selectOption = paging.selectOption;
        }
    }

    async loadData(filters?: { filterChanged: boolean }): Promise<void> {
        const loadPromise = super.loadData(filters);
        // See `loadStarted$` doc comment above: by this point `super.loadData()` has already run
        // synchronously up to (and past) its own `this.loading = true` assignment, so subscribers
        // reacting here see the CURRENT, already-flipped state — not a stale one.
        this.loadStarted$?.next();
        try {
            await loadPromise;
        } finally {
            // Some Firestore failures leave the base paging implementation in its loading
            // state. Always complete the UI lifecycle, while still allowing the original
            // rejection to propagate to the caller.
            this.loading = false;
            this.dataLoaded$?.next();
        }
    }

}

export namespace Paging {

    export interface IPagingResponse<T = any> extends PagingClass.IPagingResponse<T> { }

    export interface IPaging<T = any> extends PagingClass.IPaging<T> {
        fieldSearch?: { [field: string]: Partial<Paging.IFieldSearch> };
        onFieldSearchChange?: (...args: any[]) => void;
        selectOption?: string;
    }

    export const dataType = PagingClass.dataType;
    export type dataType = PagingClass.dataType;

    export const pagingAction = PagingClass.pagingAction;
    export type pagingAction = PagingClass.pagingAction;

    export const orderType = PagingClass.orderType;
    export type orderType = PagingClass.orderType;

    export type Precondition = { fieldPath: string, filterOp: firebase.firestore.WhereFilterOp, value: any };

    export interface IFieldSearch {
        preconditions: Paging.Precondition[],
        cb: () => any,
        executeBeforeLoadData?: boolean,
        inputType: 'text' | 'select'
    }

}
